import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Building2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import StatusBadge from "@/components/common/StatusBadge";
import UserCell from "@/components/common/UserCell";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";

export default function DepartmentDetail() {
    const { id } = useParams();
    const canView = useCan("departments.view");
    const [dept, setDept] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/departments/${id}`);
            setDept(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Departman bulunamadı.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    if (!canView) return <Forbidden />;
    if (loading || !dept) {
        return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {tr.common.loading}</div>;
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <Link to="/departments" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground mb-3">
                    <ArrowLeft className="h-3.5 w-3.5" /> {tr.departments.title}
                </Link>
                <div className="card-elevated p-6">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--brand))] text-white shadow-brand shrink-0">
                            <Building2 className="h-8 w-8" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-[24px] font-bold tracking-tight">{dept.name}</h1>
                                <StatusBadge status={dept.status || "active"} />
                            </div>
                            {dept.description && <p className="mt-2 text-[14px] text-muted-foreground">{dept.description}</p>}
                            <div className="mt-4 flex flex-wrap gap-4 text-[12.5px] text-muted-foreground">
                                <div className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> <strong className="text-foreground">{dept.employee_count}</strong> personel</div>
                            </div>
                        </div>
                        {dept.manager && (
                            <div className="rounded-2xl border border-border bg-muted/40 p-4 min-w-[220px]">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr.common.manager}</div>
                                <UserCell name={dept.manager.full_name} subtitle={dept.manager.title || dept.manager.role} avatarUrl={dept.manager.avatar_url} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <section className="card-elevated p-6">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <div className="text-[15px] font-semibold">{tr.departments.deptEmployees}</div>
                        <div className="text-[12.5px] text-muted-foreground mt-0.5">Bu departmandaki personeller</div>
                    </div>
                </div>
                {(dept.employees || []).length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground text-[13px]">Bu departmanda personel bulunmuyor.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(dept.employees || []).map((emp) => (
                            <Link to={`/personnel/${emp.id}`} key={emp.id} className="rounded-xl border border-border bg-background p-3 hover:shadow-card-hover transition-all">
                                <UserCell name={emp.full_name} subtitle={emp.title || emp.role} avatarUrl={emp.avatar_url} />
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
